import React, { MutableRefObject, useRef, useState } from 'react';

import classNames from 'classnames';

import { Icon } from './../../icon/icon';

export interface ITextFieldProps {
    id: string;
    label: string;
    name: string;
    defaultValue?: string;
    value?: string;
    type?: string;
    attributes?: React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>;
    onChange?: (value: string) => void;
    element?: 'textarea';
    elementRef?: (element: HTMLInputElement | HTMLTextAreaElement | null) => void;
    invalid?: boolean;
    disabled?: boolean;
    error?: string;
    description?: string;
    labelClassName?: string;
    inputClassName?: string;
    modifier?: string;
    className?: string;
    isStatic?: boolean;
    iconRight?: string;
    iconLeft?: string;
    iconSize?: 'small';
    focusLabel?: string;
    align?: 'center';
    hideLabel?: 'no-break' | 'sm' | 'md';
    autoFocus? : boolean;
    onFocus?: () => void;
    onBlur?: () => void;
}

const TextField = (props: ITextFieldProps): JSX.Element => {
    const {
        defaultValue = '',
        type = 'text',
    } = props;
    const element: MutableRefObject<null | HTMLElement> = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const [value, setValue] = useState(defaultValue);
    const className: string = classNames(
        'textfield',
        {
            'textfield--floating-label': !props.isStatic,
            'textfield--with-icon-right': props.iconRight,
            'textfield--with-icon-left': props.iconLeft,
            [`textfield--icon-size-${props.iconSize}`]: props.iconSize,
            [`textfield--no-label-${props.hideLabel}`]: !!props.hideLabel,
            [`textfield--${props.align}`]: !!props.align,
            'is-dirty': typeof props.value !== 'undefined' ? props.value : value,
            'is-disabled': props.disabled,
            'is-focused': isFocused,
            'is-invalid': props.invalid,
        },
        props.modifier,
        props.className,
    );

    const handleChange: (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const nextValue: string = event.currentTarget.value;

        if (typeof props.value === 'undefined') {
            setValue(nextValue);
        }

        if (props.onChange) {
            props.onChange(nextValue);
        }
    };

    const handleFocus = (): void => {
        setIsFocused(true);

        if (props.onFocus) {
            props.onFocus();
        }
    };

    const handleBlur = (): void => {
        setIsFocused(false);

        if (props.onBlur) {
            props.onBlur();
        }
    };

    const inputClassName: string = classNames('textfield__input', props.inputClassName);
    const InputElement: 'input' | 'textarea' = props.element || 'input';
    const labelClassName: string = classNames('textfield__label', props.labelClassName);

    return (
        <div className={className}>
            <div className="textfield__inner">
                {props.iconLeft && <Icon name={props.iconLeft} className="textfield__icon textfield__icon--left" />}
                <InputElement
                    {...props.attributes}
                    autoFocus={props.autoFocus}
                    className={inputClassName}
                    type={props.element === 'textarea' ? undefined : type}
                    id={props.id}
                    name={props.name}
                    disabled={props.disabled}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    // use local state only when component is not controlled from parent
                    value={typeof props.value !== 'undefined' ? props.value : value}
                    onChange={handleChange}
                    ref={(el: HTMLInputElement | HTMLTextAreaElement | null): void => {
                        if (el && element.current !== el) {
                            element.current = el;

                            if (props.elementRef) {
                                props.elementRef(el);
                            }
                        }
                    }}
                />
                <label className={labelClassName} htmlFor={props.id}>
                    {props.focusLabel && (isFocused || !!(typeof props.value !== 'undefined' ? props.value : value)) ? props.focusLabel : props.label}
                </label>
                {props.iconRight && <Icon name={props.iconRight} className="textfield__icon textfield__icon--right" />}
            </div>
            {props.error && (
                <div className="textfield__error">
                    {props.error}
                </div>
            )}
            {props.description && (
                <div className="textfield__description">
                    {props.description}
                </div>
            )}
        </div>
    );
};

export default TextField;
